import React from 'react';

import styled from '@emotion/styled';

import { COLORS } from '@clutter/clean';

type SegmentProgressBarProps = {
  total: number;
  activeIndex: number;
  progressPct: number; // 0..100
  onSelect?: (index: number) => void;
  width?: string;
  variant?: 'default' | 'partnerships';
  styling?: {
    gap?: string;
    marginBottom?: string;
    mobileGap?: string;
    mobileMarginBottom?: string;
    segmentMargin?: string;
    mobileHeight?: string;
    mobileWidth?: string;
    mobileFlexBehavior?: 'flex' | 'none';
  };
};

const Bar = styled.div<{
  width: string;
  gap: string;
  marginBottom: string;
  mobileGap: string;
  mobileMarginBottom: string;
}>`
  display: flex;
  gap: ${(p) => p.gap};
  align-items: center;
  margin-bottom: ${(p) => p.marginBottom};
  width: ${(p) => p.width};

  @media (max-width: 767px) {
    gap: ${(p) => p.mobileGap};
    margin-bottom: ${(p) => p.mobileMarginBottom};
  }
`;

const Segment = styled.div<{
  fillWidth?: number;
  animated?: boolean;
  clickable?: boolean;
  segmentMargin: string;
  mobileHeight: string;
  mobileWidth: string;
  mobileFlexBehavior: string;
}>`
  height: 8px;
  border-radius: 7.2px;
  background: ${COLORS.grayBorder};
  flex: 1;
  position: relative;
  overflow: hidden;
  cursor: ${(p) => (p.clickable ? 'pointer' : 'default')};
  margin: ${(p) => p.segmentMargin};

  @media (max-width: 767px) {
    height: ${(p) => p.mobileHeight};
    border-radius: 6px;
    width: ${(p) => p.mobileWidth};
    flex: ${(p) => p.mobileFlexBehavior};
  }

  &:hover {
    background: #d0d0d0;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${(props) => (props.fillWidth ? `${props.fillWidth}%` : '0%')};
    background: ${COLORS.tealPrimary};
    border-radius: 7.2px;
    transition: ${(p) => (p.animated ? 'width 0.1s linear' : 'none')};

    @media (max-width: 767px) {
      border-radius: 6px;
    }
  }
`;

// Preset configurations for different variants
const VARIANT_CONFIGS = {
  default: {
    gap: '3.6px',
    marginBottom: '36px',
    mobileGap: '3.6px',
    mobileMarginBottom: '36px',
    segmentMargin: '0 2px',
    mobileHeight: '8px',
    mobileWidth: 'auto',
    mobileFlexBehavior: 'flex' as const,
  },
  partnerships: {
    gap: '8px',
    marginBottom: '0px',
    mobileGap: '6px',
    mobileMarginBottom: '0',
    segmentMargin: '0',
    mobileHeight: '6px',
    mobileWidth: '106.6px',
    mobileFlexBehavior: 'none' as const,
  },
};

export const SegmentProgressBar: React.FC<SegmentProgressBarProps> = ({
  total,
  activeIndex,
  progressPct,
  onSelect,
  width = '100%',
  variant = 'default',
  styling = {},
}) => {
  const segments = Array.from({ length: total }, (_, i) => i);

  // Merge variant config with custom styling overrides
  const config = {
    ...VARIANT_CONFIGS[variant],
    ...styling,
  };

  return (
    <Bar
      width={width}
      gap={config.gap}
      marginBottom={config.marginBottom}
      mobileGap={config.mobileGap}
      mobileMarginBottom={config.mobileMarginBottom}
    >
      {segments.map((i) => {
        const isCompleted = i < activeIndex;
        const isActive = i === activeIndex;
        const fillWidth = isCompleted ? 100 : isActive ? progressPct : 0;
        return (
          <Segment
            key={i}
            fillWidth={fillWidth}
            animated={isActive}
            clickable={!!onSelect}
            segmentMargin={config.segmentMargin}
            mobileHeight={config.mobileHeight}
            mobileWidth={config.mobileWidth}
            mobileFlexBehavior={config.mobileFlexBehavior}
            onClick={() => onSelect && onSelect(i)}
            role={onSelect ? 'button' : undefined}
            tabIndex={onSelect ? 0 : -1}
            aria-label={
              onSelect ? `Go to segment ${i + 1} of ${total}` : undefined
            }
            onKeyDown={
              onSelect
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSelect(i);
                    }
                  }
                : undefined
            }
          />
        );
      })}
    </Bar>
  );
};

export type AutoProgressOptions = {
  segmentDurationMs?: number;
  tickMs?: number;
  enabled?: boolean; // when false, auto progress is paused
  resetOnEnable?: boolean; // when enabled flips to true, reset to first segment
};

export const useAutoSegmentProgress = (
  totalSegments: number,
  options: AutoProgressOptions = {},
): [{ active: number; progress: number }, (index: number) => void] => {
  const [state, setState] = React.useState<{
    active: number;
    progress: number;
  }>({ active: 0, progress: 0 });

  const setActiveIndex = React.useCallback(
    (index: number) => {
      setState({ active: index % Math.max(1, totalSegments), progress: 0 });
    },
    [totalSegments],
  );

  // Normalize options with defaults
  const { segmentDurationMs, tickMs, enabled, resetOnEnable } = React.useMemo(
    () => ({
      segmentDurationMs: options.segmentDurationMs ?? 4000,
      tickMs: options.tickMs ?? 100,
      enabled: options.enabled ?? true,
      resetOnEnable: options.resetOnEnable ?? false,
    }),
    [options],
  );

  React.useEffect(() => {
    if (!enabled) return;
    const stepsPerCycle = Math.max(1, Math.round(segmentDurationMs / tickMs));
    const id = setInterval(() => {
      setState((prev) => {
        const next = prev.progress + 100 / stepsPerCycle;
        if (next >= 100) {
          const isLast = prev.active >= totalSegments - 1;
          return { active: isLast ? 0 : prev.active + 1, progress: 0 };
        }
        return { active: prev.active, progress: next };
      });
    }, tickMs);
    return () => clearInterval(id);
  }, [totalSegments, segmentDurationMs, tickMs, enabled]);

  React.useEffect(() => {
    if (enabled && resetOnEnable) {
      setState({ active: 0, progress: 0 });
    }
  }, [enabled, resetOnEnable]);

  return [state, setActiveIndex];
};
